import { Box, Button, Menu, MenuItem } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAdminsProfile } from "src/features/organization/hooks/api/admins/use-admins-profile";
import { usePopover } from "src/hooks/use-popover";
import useStore from "src/hooks/use-store";
import { ChevronDown as ChevronDownIcon } from "src/icons/chevron-down";
import { Scrollbar } from "../atoms/scrollbar";
import { Query } from "../atoms/query";
import { t } from "i18next";
import { organizationSitesKeys } from "src/features/site-details/api/query-keys/organization-sites";
import { employeesGroupsListKeys } from "src/api/query-keys/employees-groups-list";
import { globalStatisticsKeys } from "src/features/statistics/shared/api/query-keys/global-statistics-keys";

const SEARCH_INPUT_THRESHOLD = 5;

const OrganizationSelectorComponent = () => {
  const { user } = useStore();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  const [anchorRef, open, handleOpen, handleClose] = usePopover();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const firstMenuItemRef = useRef<HTMLLIElement | null>(null);

  const [searchQuery, setSearchQuery] = useState("");

  const profileQuery = useAdminsProfile();

  const selectedOrganization = profileQuery.data?.organizations.find(
    organization => organization.token === user.userOrganizationId,
  );

  const isSearchInputThresholdCrossed = useMemo(
    () => profileQuery?.data?.organizations?.length > SEARCH_INPUT_THRESHOLD,
    [profileQuery],
  );

  const filteredOrganizations = useMemo(
    () =>
      profileQuery.data?.organizations?.filter(item =>
        item.name.toLowerCase().includes(searchQuery.trim().toLowerCase()),
      ),
    [profileQuery.data?.organizations, searchQuery],
  );

  const handleOrganizationChange = (organizationToken: string) => {
    user.setIsChangingOrganization(true);
    user.setUserOrganizationId(organizationToken);

    if (location.pathname === "/statistics") {
      const statsKeys = [
        globalStatisticsKeys.all[0],
        organizationSitesKeys.all[0],
        employeesGroupsListKeys.all[0],
      ];
      //for the statistics page only, delay invalidating stats keys in order to prevent fetch error with invalid filters - filters are being reset in the statistics page, then stats query keys are invalidated. to be reviewed upon context query keys fixing
      queryClient.invalidateQueries({
        predicate: query => {
          return !statsKeys.includes(query.queryKey[0] as any);
        },
      });
    } else {
      setTimeout(() => {
        queryClient.invalidateQueries();
      }, 200);
    }

    navigate("/statistics");
    user.setIsChangingOrganization(false);

    handleClose();
    setSearchQuery("");
  };

  useEffect(() => {
    if (!open) return;

    setTimeout(() => {
      isSearchInputThresholdCrossed ? inputRef.current?.focus() : firstMenuItemRef.current?.focus();
    });
  }, [open, isSearchInputThresholdCrossed]);

  return (
    <>
      <Button
        color="primary"
        onClick={handleOpen}
        ref={anchorRef}
        size="large"
        variant="text"
        endIcon={
          <ChevronDownIcon
            fontSize="small"
            sx={{
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease-in",
            }}
          />
        }
        sx={{
          alignItems: "center",
        }}>
        {selectedOrganization?.name ?? "---"}
      </Button>
      <Menu
        anchorEl={anchorRef.current}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        open={open}
        onClose={() => {
          handleClose();
          setSearchQuery("");
        }}
        slotProps={{
          list: {
            sx: {
              ...(isSearchInputThresholdCrossed ? { pt: 0 } : {}),
            },
          },
          paper: {
            sx: {
              border: "1px solid",
              borderColor: "divider",
              mt: 0,
            },
          },
        }}>
        {isSearchInputThresholdCrossed && (
          <Box
            sx={{
              position: "sticky",
              top: 0,
              backgroundColor: "background.paper",
              zIndex: 1,
              outline: "none",
              borderBottom: `1px solid`,
              borderColor: "divider",
            }}
            onFocus={() => {
              inputRef.current?.focus();
            }}>
            <Query
              textInputRef={inputRef}
              disabled={false}
              searchAsType
              onChange={newQuery => setSearchQuery(newQuery)}
              value={searchQuery}
              sx={{
                border: "none",
                borderRadius: 0,
                py: 2.5,
                minWidth: "100%",
                width: "min-content",
              }}
              onKeyDown={e => {
                if (e.key !== "ArrowDown") {
                  e.stopPropagation();
                } else {
                  setTimeout(() => {
                    firstMenuItemRef.current?.focus();
                  }, 0);
                }
              }}
            />
          </Box>
        )}

        <Scrollbar
          sx={{
            maxHeight: "60vh",
            width: "max-content",
            pt: isSearchInputThresholdCrossed ? 1 : 0,
          }}>
          {filteredOrganizations?.length > 0 ? (
            filteredOrganizations?.map((organization, idx) => {
              return (
                <MenuItem
                  ref={idx === 0 ? firstMenuItemRef : null}
                  key={organization.token}
                  onClick={() => {
                    handleOrganizationChange(organization.token);
                  }}
                  sx={{ minWidth: "fit-content" }}>
                  {organization.name}
                </MenuItem>
              );
            })
          ) : (
            <MenuItem disabled>{t("common:noResults")}</MenuItem>
          )}
        </Scrollbar>
      </Menu>
    </>
  );
};

export const OrganizationSelector = observer(OrganizationSelectorComponent);
